import { observer, inject } from "mobx-react";
import React, { useEffect, useState } from "react";
import MaterialTable from "material-table";
import { Typography, IconButton, Button, Grid, DialogContent, DialogActions } from "@material-ui/core";
import Link from "@material-ui/core/Link";
import { HtmlTooltip } from "../HtmlTooltip/HtmlTooltip";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";

export const RenderManualDataForPreview = inject("RootStore")(
  observer(({ RootStore, ...props }) => {
    const [activeButton, setActiveButton] = useState("employee");

    useEffect(() => {}, [RootStore.qarStore.selectedAccountType]);

    const tableOptions = {
      filtering: false,
      grouping: false,
      draggable: false,
      paging: true,
      search: true,
      toolbar: true,
      showTitle: false,
      sorting: true,
      pageSize: 50,
      pageSizeOptions: [50, 100, 200],
    };

    if (props.disabled) {
      return <></>;
    }

    let data = RootStore.qarStore.manualDataForPreview?.employeeAccounts;

    if (RootStore.qarStore.selectedAccountType === "employeeAccounts") {
      data = RootStore.qarStore.manualDataForPreview?.employeeAccounts;
    } else if (RootStore.qarStore.selectedAccountType === "unmatchedAccounts") {
      data = RootStore.qarStore.manualDataForPreview?.unmatchedAccounts;
    }

    if (data === undefined) {
      return;
    }

    let roleName = "";

    const handleRoleClick = async (event, role) => {
      event.preventDefault();
      await RootStore.qarStore.getPermissionsForRoleAsync(role.id);
      roleName = role.name;
      RootStore.uiStateStore.setModalContent(getRolePermissionGroupsTable());
      RootStore.uiStateStore.setModalOpen(true);
    };

    const getRolePermissionGroupsTable = () => {
      const rolePermissionInfo = RootStore.qarStore.permissionsForRole;

      const columns = [{ title: "Name", field: "name", defaultSort: "asc" }];

      return (
        <>
          <h2>
            Role '{roleName}' has a total of{" "}
            {rolePermissionInfo.rolePermissions.length} permission{" "}
            {rolePermissionInfo.rolePermissions.length !== 1
              ? "groups"
              : "group"}
          </h2>
          {rolePermissionInfo.rolePermissions.length !== 0 ?
          <MaterialTable
            options={{
              ...tableOptions,
              filtering: true,
              pageSizeOptions: [10, 20, 50],
            }}
            data={rolePermissionInfo.rolePermissions}
            columns={columns}
          />:
          <></>}
        </> 
      );
    };

    const getUserPermissionGroupsTable = (employeeAccount) => {
      const permissionObjects = employeeAccount.employeeAccountPermissions.map(
        (permission) => ({ permission: permission })
      );

      return (
        <>
          <h2>
            User '{employeeAccount.username}' has a total of{" "}
            {employeeAccount.employeeAccountPermissions.length} permission{" "}
            {employeeAccount.employeeAccountPermissions.length !== 1
              ? "groups"
              : "group"}
          </h2>
          {permissionObjects.length !== 0 ?
          <MaterialTable
            options={{
              ...tableOptions,
              filtering: true,
              pageSizeOptions: [10, 20, 50],
            }}
            data={permissionObjects}
            columns={[
              {
                title: "Name",
                field: "permission",
                render: (rowData) => rowData.permission,
                defaultSort: "asc",
              },
            ]}
          />:
          <></>}
        </>
      );
    };

    const handleUsernameClick = (event, employeeAccount) => {
      event.preventDefault(); // this prevents appending hashtag to the url
      RootStore.uiStateStore.setModalContent(
        getUserPermissionGroupsTable(employeeAccount)
      );
      RootStore.uiStateStore.setModalOpen(true);
    };

    const handleEmployeeAccountsClick = () => {
      RootStore.qarStore.selectedAccountType = "employeeAccounts";
      setActiveButton("employee");
    };

    const handleServiceAccountsClick = () => {
      RootStore.qarStore.selectedAccountType = "unmatchedAccounts";
      setActiveButton("service");
    };

    const confirmationModalContent = () =>{
      return <div>
            <h2>Are you sure?</h2>
            <DialogContent dividers={false}>
                <p>By clicking confirm all of the uploaded accounts will be saved into the Database.</p>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => RootStore.uiStateStore.clearModal()} color="primary">
                    Cancel
                </Button>
                <Button onClick={() => confirmSave()} color="primary">
                    Confirm
                </Button>
            </DialogActions>
        </div>
    }

    const confirmSave = async () =>{
      await RootStore.qarStore.MarkSystemsAsReviewed();
      await RootStore.qarStore.getManualDataForPreview();
      RootStore.uiStateStore.clearModal()
      RootStore.uiStateStore.setModalOpen(false);
    }

    const importClickHandler = () => {
      RootStore.uiStateStore.setModalContent(confirmationModalContent());
      RootStore.uiStateStore.setModalOpen(true);
    };

    const columns = [
      { title: "System", field: "system", defaultSort: "asc", width: "8%" },
      { title: "Account", field: "account", defaultSort: "asc", width: "8%" },
      { title: "Name", field: "fullName", defaultSort: "asc", width: "10%" },
      {
        title: "Username",
        field: "username",
        render: (rowData) => (
          <>
            <Link
              href="#"
              onClick={(event) => handleUsernameClick(event, rowData)}
            >
              {rowData.username}
            </Link>
            <HtmlTooltip
              title={
                <>
                  <Typography color="inherit" variant="caption">
                    Click on username to see its permission groups.
                  </Typography>
                </>
              }
            >
              <IconButton aria-label="delete" size="small">
                <HelpOutlineIcon fontSize="inherit" />
              </IconButton>
            </HtmlTooltip>
          </>
        ),
        defaultSort: "asc",
        width: "13%"
      },
      { title: "Email", field: "email", defaultSort: "asc", width: "12%" },
      {
        title: "Roles",
        field: "roles",
        render: (rowData) => (
          <>
            {rowData.roles?.map((x) => (
              <>
                <Link href="#" onClick={(ev) => handleRoleClick(ev, x)}>
                  {x.name}
                </Link>
                <HtmlTooltip
                  title={
                    <>
                      <Typography color="inherit" variant="caption">
                        Click on role name to see its groups.
                      </Typography>
                    </>
                  }
                >
                  <IconButton aria-label="delete" size="small">
                    <HelpOutlineIcon fontSize="inherit" />
                  </IconButton>
                </HtmlTooltip>
              </>
            ))}
          </>
        ),
        defaultSort: "asc",
        width: "27%"
      },
      { title: "Manager Email", field: "managerEmail", defaultSort: "asc", width: "12%" },
      { title: "Manager Name", field: "managerNames", defaultSort: "asc", width: "10%" },
    ];

    return (
      <>
        <div class="centered-container">
          <h2>Manual Data Upload Preview</h2>
          <Grid container spacing={2}>
            <Grid item>
              <Button
                variant="contained"
                color={activeButton === "employee" ? "primary" : "default"}
                onClick={() => handleEmployeeAccountsClick()}
              >
                Employee Accounts
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                color={activeButton === "service" ? "primary" : "default"}
                onClick={() => handleServiceAccountsClick()}
              >
                Not Recognized Accounts
              </Button>
            </Grid>
          </Grid>
        </div>
        {data.length !== 0 ? 
        <MaterialTable
          options={tableOptions}
          data={data}
          columns={columns}
        /> : 
        <p>No Data To Display</p>
  }
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="contained"
            color="primary"
            style={{
              margin: "1%",
            }}
            onClick={importClickHandler}
            disabled={!RootStore.qarStore.manualDataForPreview.manualDataInPreview}
          >
            Confirm and import data
          </Button>
        </div>
      </>
    );
  })
);
